/** @format */

import { useEffect, useState } from 'react';

import { Formik } from 'formik';
import { PatternFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Logo from '../../Assets/Images/logo.jpg';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import SwitchButton from '../../Shared/Components/SwitchButton';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { storeAuthToken } from '../../Shared/utils/authToken';

const validateNationalId = (value, isForeign) => {
  const id = (value || '').trim();

  if (!id) {
    return isForeign ? 'کد اتباع را وارد کنید' : 'کد ملی را وارد کنید';
  }

  if (isForeign) {
    if (id.length < 5) {
      return 'کد اتباع نمی‌تواند کمتر از ۵ کاراکتر باشد';
    }
    if (id.length > 20) {
      return 'کد اتباع نمی‌تواند بیشتر از ۲۰ کاراکتر باشد';
    }
    if (!/^[0-9A-Za-z]+$/.test(id)) {
      return 'کد اتباع فقط می‌تواند شامل حروف و اعداد باشد';
    }
    return undefined;
  }

  if (id.length !== 10) {
    return 'کد ملی باید ۱۰ رقم باشد';
  }

  return undefined;
};

const validateRegisterForm = (values) => {
  const errors = {};
  const isForeign = Boolean(values.is_foreign);

  const nationalError = validateNationalId(values.national_card_id, isForeign);
  if (nationalError) {
    errors.national_card_id = nationalError;
  }

  if (!values.mobile) {
    errors.mobile = 'لطفا موبایل را وارد نمایید';
  } else if (String(values.mobile).length !== 11) {
    errors.mobile = 'موبایل نمیتواند کمتر از 11 کاراکتر باشد';
  }

  if (!values.password) {
    errors.password = 'کلمه عبور نمی تواند خالی باشد';
  } else if (values.password.length < 8) {
    errors.password = 'کلمه عبور نمیتواند کمتر از8 کاراکتر باشد';
  } else if (values.password.length > 64) {
    errors.password = 'کلمه عبور نباید بیشتر از 64 کاراکتر باشد';
  }

  if (values.password_confirmation !== undefined && values.password_confirmation !== values.password) {
    errors.password_confirmation = 'کلمه عبور و تکرار آن باید یکسان باشد.';
  }

  return errors;
};

const validationSchemaVerify = Yup.object().shape({
  code: Yup.string()
    // .matches(/^([09])([0-9\s]{11})$/, 'موبایل به درستی وارد نشده است')
    .required('لطفا کد تایید را وارد نمایید')
    .min(5, 'کد تایید نمیتواند کمتر از 5 کاراکتر باشد')
    .max(9, 'کد تایید نمیتواند بیشتر از 5 کاراکتر باشد'),
});

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState();
  const [showActivationForm, setShowActivationForm] = useState(false);
  const [rulesChecked, setRulesChecked] = useState(false);
  const [natCode, setNatCode] = useState(undefined);

  useEffect(() => {}, [loading]);

  const onSubmitRegisterForm = async (values, setSubmitting) => {
    if (!rulesChecked)
      return error('لطفا شرایط ثبت نام را خوانده و تایید نمایید.');

    setLoading(true);
    const payload = {
      national_card_id: values.national_card_id,
      mobile: values.mobile,
      password: values.password,
      is_foreign: Boolean(values.is_foreign),
    };
    await useApi.post(
      '/register',
      payload,
      (res) => {
        setNatCode(res.data?.data?.national_card_id);
        success('کد تایید با موفقیت ارسال شد.');
        setSubmitting(false);
        setLoading(false);
        setShowActivationForm(true);
      },
      (err) => {
        setSubmitting(false);
        setLoading(false);
        if (err?.status === 422) {
          Object.values(err?.data?.errors)?.forEach((item) => {
            error(item[0]);
          });
        } else {
          return error('خطای ناخواسته ای رخ داده است');
        }
      },
    );
  };

  const toEnglishDigits = (value) =>
    String(value || '')
      .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
      .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

  const onSubmitVerifyForm = async (values, setSubmitting) => {
    setSubmitting(true);
    setLoading(true);
    const payload = {
      nat_code: toEnglishDigits(natCode),
      code: toEnglishDigits(values.code).replaceAll(' ', ''),
    };
    try {
      await useApi.post(
        '/verify',
        payload,
        (res) => {
          const token = res?.data?.data?.token;
          const message =
            res?.data?.data?.message || 'حساب کاربری شما با موفقیت فعال شد.';

          if (!token) {
            success(message);
            return navigate('/login');
          }

          storeAuthToken(token);
          success(message);
          window.location.href = '/';
        },
        (err) => {
          if (err?.status === 422) {
            return error(err?.data?.message || 'کد تایید نامعتبر است');
          }
          return error(err?.data?.message || 'خطای ناخواسته ای رخ داده است');
        },
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return showActivationForm ? (
    <div className='w-100 d-flex flex-column justify-content-center align-items-center vh-100 login-page'>
      <section className='container'>
        <div className='shadow-lg rounded-2 bg-white overflow-y-auto p-2'>
          <Formik
            initialValues={{
              code: '',
            }}
            onSubmit={(values, { setSubmitting }) => {
              onSubmitVerifyForm(values, setSubmitting);
            }}
            validationSchema={validationSchemaVerify}
          >
            {({ values, errors, handleChange, handleSubmit, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <div className='row'>
                  <div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 '>
                    <div className='d-flex flex-column p-2'>
                      <div className='d-flex flex-column p-2 align-content-center justify-content-center'>
                        <div className='w-100 d-flex flex-column justify-content-center align-items-center'>
                          <img className='w-15' src={Logo} alt='logo' />
                        </div>
                      </div>
                      <div className='d-flex flex-column justify-content-center text-center my-3'>
                        <h3 className='text-dark f-14'>
                          {' '}
                          فعال سازی حساب کاربری{' '}
                        </h3>
                      </div>

                      <div className='d-flex flex-row my-3'>
                        <hr className='w-30 bg-hr' />
                        <span className='text-dark f-14 align-self-center'>
                          کد تایید خود را وارد نمایید
                        </span>
                        <hr className='w-30 bg-hr' />
                      </div>
                    </div>
                  </div>

                  <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                    <div
                      className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                      style={{ height: '40px' }}
                    >
                      <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                        <label className='f-13 text-left undefined d-flex flex-row justify-content-start mx-1'>
                          کد تایید
                        </label>
                        <PatternFormat
                          format='# # # # #'
                          className={`form-control bg-none border-0 text-center rounded-1 m-0 dir-ltr p-0 ${
                            errors.code ? 'invalid-input' : ''
                          }`}
                          mask='_'
                          name='code'
                          value={values.code}
                          onChange={handleChange}
                        />
                        <ErrorMessage error={errors.code} classes='my-2 f-13' />
                      </div>
                      <div
                        class='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                        style={{
                          position: 'absolute',
                          top: '-16px',
                          left: '0px',
                        }}
                      >
                        <i class='far fa-mobile f-18'></i>
                      </div>
                    </div>
                  </div>

                  <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                    <div className='form-group d-flex flex-column'>
                      <button
                        className='btn bg-blue-sky-fill border rounded-1 w-100'
                        type='submit'
                        disabled={isSubmitting}
                      >
                        {loading ? (
                          <div
                            className='spinner-border spinner-border-sm text-white mb-1'
                            role='status'
                          ></div>
                        ) : (
                          'فعال سازی'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </section>
    </div>
  ) : (
    <>
      <div className='w-100 d-flex flex-column justify-content-center align-items-center vh-100 login-page'>
        <section className='container'>
          <div className='shadow-lg rounded-2 bg-white overflow-y-auto p-2'>
            {' '}
            <Formik
              initialValues={{
                national_card_id: '',
                mobile: '',
                password: '',
                password_confirmation: '',
                is_foreign: false,
              }}
              onSubmit={(values, { setSubmitting }) => {
                onSubmitRegisterForm(values, setSubmitting);
              }}
              validate={validateRegisterForm}
            >
              {({
                values,
                errors,
                handleChange,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                setFieldError,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className='row '>
                    <div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 '>
                      <div className='d-flex flex-column p-2'>
                        <div className='d-flex flex-column p-2 align-content-center justify-content-center'>
                          <div className='w-100 d-flex flex-column justify-content-center align-items-center'>
                            <img className='w-15' src={Logo} alt='logo' />
                          </div>
                        </div>
                        <div className='d-flex flex-column justify-content-center text-center my-3'>
                          <h3 className='text-dark f-14'>ثبت نام در سامانه</h3>
                        </div>

                        <div className='d-flex flex-row my-3'>
                          <hr className='w-30 bg-hr' />
                          <span className='text-dark f-14 align-self-center'>
                            فرم زیر را تکمیل کنید و روی ثبت نام کلیک کنید
                          </span>
                          <hr className='w-30 bg-hr' />
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left undefined d-flex flex-row justify-content-start mx-1'>
                            {values.is_foreign ? 'کد اتباع' : 'کد ملی'}
                          </label>
                          <input
                            type='text'
                            value={values.national_card_id}
                            name='national_card_id'
                            onChange={handleChange}
                            className='form-control bg-none border-0 rounded-1 m-0 p-0'
                          />
                          <ErrorMessage
                            error={errors.national_card_id}
                            classes='my-2 f-13'
                          />
                        </div>
                        <div
                          className='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{
                            position: 'absolute',
                            top: '-16px',
                            left: '0px',
                          }}
                        >
                          <i className='far fa-user f-18'></i>
                        </div>
                      </div>
                      <div className='form-check d-flex flex-row align-items-center mb-2'>
                        <input
                          type='checkbox'
                          className='form-check-input mt-0'
                          id='register-is-foreign'
                          checked={Boolean(values.is_foreign)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFieldValue('is_foreign', checked, false);
                            if (errors.national_card_id) {
                              const nextError = validateNationalId(
                                values.national_card_id,
                                checked,
                              );
                              setFieldError(
                                'national_card_id',
                                nextError || undefined,
                              );
                            } else {
                              setFieldError('national_card_id', undefined);
                            }
                          }}
                        />
                        <label
                          className='form-check-label f-13 text-dark mr-2'
                          htmlFor='register-is-foreign'
                        >
                          اتباع هستم
                        </label>
                      </div>
                    </div>
                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left undefined d-flex flex-row justify-content-start mx-1'>
                            شماره همراه
                          </label>
                          <input
                            type='text'
                            value={values.mobile}
                            name='mobile'
                            onChange={handleChange}
                            className='form-control bg-none border-0 rounded-1 m-0 p-0'
                          />
                          <ErrorMessage
                            error={errors.mobile}
                            classes='my-2 f-13'
                          />
                        </div>
                        <div
                          class='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{
                            position: 'absolute',
                            top: '-16px',
                            left: '0px',
                          }}
                        >
                          <i class='far fa-mobile f-18'></i>
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left undefined d-flex flex-row justify-content-start mx-1'>
                            کلمه عبور
                          </label>
                          <input
                            type='password'
                            value={values.password}
                            name='password'
                            onChange={handleChange}
                            className='form-control bg-none border-0 rounded-1 m-0 p-0'
                          />
                          <ErrorMessage
                            error={errors.password}
                            classes='my-2 f-13'
                          />
                        </div>
                        <div
                          class='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{
                            position: 'absolute',
                            top: '-16px',
                            left: '0px',
                          }}
                        >
                          <i class='far fa-lock f-18'></i>
                        </div>
                      </div>
                    </div>
                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left undefined d-flex flex-row justify-content-start mx-1'>
                            کلمه عبور
                          </label>
                          <input
                            type='password'
                            value={values.password_confirmation}
                            name='password_confirmation'
                            onChange={handleChange}
                            className='form-control bg-none border-0 rounded-1 m-0 p-0'
                          />
                          <ErrorMessage
                            error={errors.password_confirmation}
                            classes='my-2 f-13'
                          />
                        </div>
                        <div
                          class='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{
                            position: 'absolute',
                            top: '-16px',
                            left: '0px',
                          }}
                        >
                          <i class='far fa-lock f-18'></i>
                        </div>
                      </div>
                    </div>

                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                      <div className='form-group d-flex flex-column'>
                        <label className='text-dark f-15 text-left my-2 d-flex flex-row'>
                          <SwitchButton
                            checked={rulesChecked}
                            classes='mx-2'
                            onChange={() => setRulesChecked(!rulesChecked)}
                          />
                          <span className='align-self-baseline'>
                            با
                            <a
                              target='_blank'
                              className='text-info c-pointer mx-1'
                              href='https://google.com' rel="noreferrer"
                            >
                              شرایط
                            </a>{' '}
                            موافقم
                          </span>
                        </label>
                        <ErrorMessage
                          error={errors.rules}
                          classes='my-2 f-13'
                        />
                      </div>
                    </div>
                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3 offset-lg-3 offset-xl-3'>
                      <div className='form-group d-flex flex-column'>
                        <button
                          className='btn bg-blue-sky-fill border rounded-1 w-100'
                          type='submit'
                        >
                          {loading ? (
                            <div
                              className='spinner-border spinner-border-sm text-white mb-1'
                              role='status'
                            ></div>
                          ) : (
                            'ثبت نام'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </section>
      </div>
    </>
  );
};
export default Register;
